import React, { useEffect, useState } from "react";
import { DocumentAttachment, DocumentDownload } from "@carbon/icons-react";
import { bytesToKilobytes, download } from "../../utils/file";
import { Modal } from "./Modal";
import { useUserContext } from "../../utils/contexts/UserProvider";
import { useTranslation } from "react-i18next";
import { getAgreementFile, getGdprFile } from "../../services/apiHelpers";
import { ServerFile } from "../../types/components";
import { usePutResource } from "../../services";
import { DataAgreementModal } from "./DataAgreementModal";
import {
  getAgentParticipantWithoutAcceptedAgreement,
  hasNotAcceptedAgreements,
} from "../../utils/user";
import { ParticipantAgent } from "../../types/context";

export const GdprClauseModal: React.FC = () => {
  const { user, setUser } = useUserContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [gdprFile, setGdprFile] = useState<{ text: string; file: ServerFile } | null>(null);
  const { t } = useTranslation();
  const { updateResource: updateUserResource } = usePutResource("/common/user");
  const { updateResource: updateAgentParticipantResource } = usePutResource(
    "/common/agent-participant",
  );
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  const [agreementFile, setAgreementFile] = useState<{
    id: string;
    file: ServerFile;
    text?: string;
    agentParticipant?: ParticipantAgent;
  } | null>(null);

  const requestAgreementFile = async () => {
    await updateUserResource({
      resourceId: user?.id,
      is_confirming_data: true,
    });

    setUser((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        is_agreement_accepted: false,
        participant_agents: prev.participant_agents.map((participantAgent) => ({
          ...participantAgent,
          latest_accepted_agreement: null,
        })),
      };
    });

    const [agreementFile]: [
      {
        id: string;
        file: ServerFile;
        text: string;
      } | null,
    ] = await getAgreementFile(user?.id);

    setAgreementFile(agreementFile);
    setIsAgreementModalOpen(true);
  };

  const handleConfirmAgreement = async () => {
    if (!user) {
      return;
    }

    const localUser = user;

    if (!agreementFile?.agentParticipant) {
      await updateUserResource({
        resourceId: user?.id,
        is_agreement_accepted: true,
        is_accepting_agreement: true,
      });

      localUser.is_agreement_accepted = true;
      setUser(localUser);
    }

    if (agreementFile?.agentParticipant) {
      await updateAgentParticipantResource({
        resourceId: agreementFile?.agentParticipant?.id,
        is_accepting_agreement: true,
      });

      localUser.participant_agents = localUser.participant_agents.map((participantAgent) =>
        participantAgent.id === agreementFile?.agentParticipant?.id
          ? {
              ...participantAgent,
              latest_accepted_agreement: agreementFile.file,
            }
          : participantAgent,
      );
      setUser((prev) => ({ ...prev, ...localUser }));
    }

    if (hasNotAcceptedAgreements(localUser)) {
      const agentParticipant = getAgentParticipantWithoutAcceptedAgreement(localUser);

      if (!agentParticipant) {
        setIsAgreementModalOpen(false);
        return;
      }

      const { data } = (await updateAgentParticipantResource({
        resourceId: agentParticipant.id,
        is_confirming_data: true,
        params: "?include=latest_agreement,latest_agreement.file",
      })) as { data: ParticipantAgent };

      setAgreementFile(
        data?.latest_agreement?.file
          ? { id: data?.id, file: data?.latest_agreement?.file, agentParticipant }
          : null,
      );
      setIsAgreementModalOpen(true);

      return;
    }

    setIsAgreementModalOpen(false);
  };

  useEffect(() => {
    if (!user?.is_gdpr_accepted) {
      getGdprFile(user?.id).then((resp: [{ id: string; text: string; file: ServerFile }]) => {
        setGdprFile(resp?.[0]);
        setIsModalOpen(true);
      });
    }
  }, [user]);

  return (
    <>
      <Modal
        isModalOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title={t("GDPR Clause")}
        subtitle={""}
        additionalClassName="sm:max-w-4xl"
        hideCloseButton
        secondButtonLabel={t("Accept Clause")}
        secondButtonType={"primary"}
        secondButtonClick={async () => {
          await updateUserResource({
            resourceId: user?.id,
            is_gdpr_accepted: true,
            latest_gdpr_file_id: gdprFile?.file?.id,
          });

          setUser((prev) => prev && { ...prev, is_gdpr_accepted: true });
          setIsModalOpen(false);

          if (user?.is_agreement_accepted) {
            return;
          }

          await requestAgreementFile();
        }}
        content={
          <div className="flex flex-col gap-4">
            {gdprFile?.text && (
              <iframe height="400px" src="about:blank" srcDoc={gdprFile?.text}></iframe>
            )}

            <div className="flex items-start justify-start bg-ghost-110 rounded-md p-4 border border-gray-100 gap-3">
              <div>
                <DocumentAttachment size={40} />
              </div>
              <div className="w-full">
                <div className="flex justify-between items-center w-full">
                  <p className="text-black truncate text-[16px] font-normal leading-[20.16px]">
                    {gdprFile?.file?.file_name}
                  </p>
                  <button onClick={() => download(gdprFile?.file ?? undefined)}>
                    <DocumentDownload className="text-ominimo-400" size={20} />
                  </button>
                </div>

                {gdprFile?.file?.file_size && (
                  <p className="text-gray-550 truncate text-[15px] font-light leading-[18.9px]">
                    {bytesToKilobytes(gdprFile?.file?.file_size)} KB
                  </p>
                )}
              </div>
            </div>
          </div>
        }
      />
      <DataAgreementModal
        isAgreementModalOpen={isAgreementModalOpen}
        setIsAgreementModalOpen={setIsAgreementModalOpen}
        handleConfirmAgreement={handleConfirmAgreement}
        agreementFile={agreementFile?.file}
      />
    </>
  );
};
